import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  FormControlLabel,
  Checkbox,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  CreateExamSimulatorQuestionCall,
  DeleteExamSimulatorQuestionCall,
  GetExamSimulatorQuestionsCall,
  UpdateExamSimulatorQuestionCall,
} from "../../redux/api/examSimulator.api";
import { GetTitlesCall } from "../../redux/api/title.api";
import { openAppConfirm } from "../../components/AppDialogs";
import ExamSimulatorQuestionMarkerMap from "./ExamSimulatorQuestionMarkerMap";
import {
  EXAM_SIMULATOR_MAX_QUESTIONS_PER_SLIDE,
  buildNewQuestionForm,
  countQuestionsForSlide,
  nextQuestionIndexForSlide,
} from "./examSimulatorQuestionTemplates";

const CATEGORIES = [
  { value: "histology", label: "Histology", titleType: "his" },
  { value: "embryology", label: "Embryology", titleType: "emb" },
];

const emptyForm = () => ({
  id: null,
  category: "histology",
  slide_id: "",
  question_index: 1,
  question_text: "",
  expected_answer: "",
  video_start_sec: 0,
  video_end_sec: 0,
  wait_start_sec: 0,
  wait_end_sec: 0,
  slide_video_end_sec: "",
  marker_location: null,
  is_active: true,
  sort_order: 0,
});

function titleDisplayName(node) {
  const locs = node?.localizations || node?.Localizations || [];
  const en = locs.find((l) => String(l?.lang || "").toLowerCase() === "en");
  return (
    en?.title ||
    en?.name ||
    locs[0]?.title ||
    locs[0]?.name ||
    node?.name ||
    node?.title ||
    "Untitled"
  );
}

function slidePreviewUrl(file) {
  if (!file?.url) return null;
  const base = String(file.url).replace(/\/+$/, "");
  const name = String(file.name || file.alias || "").toLowerCase();
  const type = String(file.type || "").toLowerCase();
  const ext =
    name.includes("upload-photo") ||
    /\.jpe?g$/.test(name) ||
    type.includes("jpeg")
      ? "jpg"
      : "png";
  return `${base}/3/3/3.${ext}`;
}

function SlideThumb({ url, size = 56 }) {
  if (!url) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: 1.5,
          bgcolor: "action.hover",
          flexShrink: 0,
        }}
      />
    );
  }
  return (
    <Box
      component="img"
      src={url}
      alt=""
      sx={{
        width: size,
        height: size,
        objectFit: "cover",
        borderRadius: 1.5,
        flexShrink: 0,
        bgcolor: "action.hover",
        border: (t) => `1px solid ${t.palette.divider}`,
      }}
      onError={(e) => {
        e.currentTarget.style.visibility = "hidden";
      }}
    />
  );
}

/**
 * Admin Exam Simulator bank — author oral-exam questions with
 * continuous-avatar video timing windows (ask + wait/loop).
 */
export default function ExamSimulatorAdminPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [category, setCategory] = useState("histology");
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [slideOptions, setSlideOptions] = useState([]);
  const [slidesLoading, setSlidesLoading] = useState(false);
  const [advancedEdit, setAdvancedEdit] = useState(false);

  const inputBg = isDark
    ? theme.palette.grey?.[800] ?? "#2d2d2d"
    : theme.palette.background.paper;
  const inputBorder = theme.palette.divider;
  const fieldMinHeight = 52;
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: inputBg,
      color: theme.palette.text.primary,
      minHeight: fieldMinHeight,
      "& fieldset": { borderColor: inputBorder },
    },
    "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
  };
  const autocompleteSx = {
    ...inputSx,
    "& .MuiAutocomplete-inputRoot": {
      minHeight: fieldMinHeight,
      py: 0.5,
    },
  };

  const loadQuestions = useCallback(async () => {
    const res = await GetExamSimulatorQuestionsCall({ category });
    if (res?.status === 200 && Array.isArray(res.data)) {
      setItems(res.data);
    } else {
      setItems([]);
    }
  }, [category]);

  const loadSlides = useCallback(async () => {
    setSlidesLoading(true);
    try {
      const meta = CATEGORIES.find((c) => c.value === category);
      const res = await GetTitlesCall("en", meta?.titleType || "his");
      const titles = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];
      const opts = [];
      const walk = (nodes, parentPath = "") => {
        if (!Array.isArray(nodes)) return;
        for (const node of nodes) {
          const name = titleDisplayName(node);
          const path = parentPath ? `${parentPath} / ${name}` : name;
          const slides = node?.slides || node?.Slides || [];
          for (const s of slides) {
            if (!s?.id) continue;
            const file =
              (s.Files && s.Files[0]) ||
              (s.files && s.files[0]) ||
              null;
            const thumb = slidePreviewUrl(file);
            opts.push({
              value: s.id,
              label: path,
              searchText: `${path} ${s.id}`.toLowerCase(),
              thumb,
              fileUrl: file?.url || null,
              fileName: file?.name || null,
              fileAlias: file?.alias || null,
              fileType: file?.type || null,
              maxZoom: s.max_zoom ?? s.maxZoom ?? 4,
            });
          }
          walk(node?.subtitles || node?.children || [], path);
        }
      };
      walk(titles);
      setSlideOptions(opts);
    } finally {
      setSlidesLoading(false);
    }
  }, [category]);

  useEffect(() => {
    loadQuestions();
    loadSlides();
  }, [loadQuestions, loadSlides]);

  const selectedCategory =
    CATEGORIES.find((c) => c.value === category) || CATEGORIES[0];

  const selectedSlide =
    slideOptions.find((o) => o.value === form.slide_id) || null;

  const slideMetaById = useMemo(() => {
    const map = new Map();
    for (const o of slideOptions) map.set(o.value, o);
    return map;
  }, [slideOptions]);

  const slideQuestionCount = form.slide_id
    ? countQuestionsForSlide(items, form.slide_id, category)
    : 0;

  const slideAtMax =
    !form.id && slideQuestionCount >= EXAM_SIMULATOR_MAX_QUESTIONS_PER_SLIDE;

  const timingLocked = !advancedEdit;

  const questionIndexNum = Number(form.question_index) || 1;
  const isFourthQuestion =
    questionIndexNum === EXAM_SIMULATOR_MAX_QUESTIONS_PER_SLIDE;
  const showMarkerMap = Boolean(selectedSlide?.fileUrl) && isFourthQuestion;
  const markerMapDisabled = timingLocked && !isFourthQuestion;

  useEffect(() => {
    if (form.id || !form.slide_id) return;
    const built = buildNewQuestionForm({
      category,
      slideId: form.slide_id,
      items,
    });
    if (!built) {
      setMessage("This slide already has 4 questions.");
      return;
    }
    setForm((f) => ({
      ...f,
      ...built,
      slide_id: form.slide_id,
      category,
    }));
    setMessage("");
  }, [form.slide_id, category, form.id, slideQuestionCount]);

  const startAddQuestionForSlide = (slideId) => {
    setAdvancedEdit(false);
    const built = buildNewQuestionForm({ category, slideId, items });
    if (!built) {
      setMessage("This slide already has 4 questions.");
      return;
    }
    setForm(built);
    setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const grouped = useMemo(() => {
    const map = new Map();
    for (const q of items) {
      const key = q.slide_id;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(q);
    }
    return [...map.entries()];
  }, [items]);

  const onSave = async () => {
    if (!form.id && slideAtMax) {
      setMessage("This slide already has 4 questions.");
      return;
    }
    setSaving(true);
    setMessage("");
    const body = {
      ...form,
      category,
      question_index: Number(form.question_index) || 1,
      video_start_sec: Number(form.video_start_sec) || 0,
      video_end_sec: Number(form.video_end_sec) || 0,
      wait_start_sec: Number(form.wait_start_sec) || 0,
      wait_end_sec: Number(form.wait_end_sec) || 0,
      slide_video_end_sec:
        form.slide_video_end_sec === "" || form.slide_video_end_sec == null
          ? null
          : Number(form.slide_video_end_sec) || 0,
      sort_order: Number(form.sort_order) || 0,
    };
    const res = form.id
      ? await UpdateExamSimulatorQuestionCall(form.id, body)
      : await CreateExamSimulatorQuestionCall(body);
    setSaving(false);
    if (res?.status === 200 || res?.status === 201) {
      setMessage(form.id ? "Updated." : "Created.");
      setForm({ ...emptyForm(), category });
      setAdvancedEdit(false);
      loadQuestions();
    } else {
      setMessage(res?.message || "Save failed.");
    }
  };

  const onEdit = (q) => {
    setAdvancedEdit(false);
    setForm({
      id: q.id,
      category: q.category,
      slide_id: q.slide_id,
      question_index: q.question_index,
      question_text: q.question_text,
      expected_answer: q.expected_answer,
      video_start_sec: q.video_start_sec,
      video_end_sec: q.video_end_sec,
      wait_start_sec: q.wait_start_sec,
      wait_end_sec: q.wait_end_sec,
      slide_video_end_sec: q.slide_video_end_sec ?? "",
      marker_location: q.marker_location || null,
      is_active: q.is_active,
      sort_order: q.sort_order,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onDelete = async (q) => {
    const ok = await openAppConfirm({
      title: "Delete question?",
      message: q.question_text?.slice(0, 120) || q.id,
    });
    if (!ok) return;
    await DeleteExamSimulatorQuestionCall(q.id);
    loadQuestions();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={2}>
        <Stack spacing={1} mb={3}>
          <Typography variant="h4" fontWeight={800} color="text.primary">
            Exam Simulator
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Author oral-exam questions for histology & embryology. Timing is
            relative to the continuous <strong>questions.mp4</strong> avatar
            video — question segment (start→end) then wait/loop segment while
            the learner answers.
          </Typography>
        </Stack>

        <MDBox
          mb={3}
          p={2.5}
          borderRadius="16px"
          sx={{
            background:
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, rgba(27,139,255,0.12), rgba(15,23,42,0.6))"
                : "linear-gradient(135deg, rgba(27,139,255,0.08), rgba(255,255,255,0.9))",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={CATEGORIES}
                value={selectedCategory}
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(a, b) => a.value === b.value}
                onChange={(_, next) => {
                  const value = next?.value || "histology";
                  setCategory(value);
                  setAdvancedEdit(false);
                  setForm({ ...emptyForm(), category: value });
                }}
                disableClearable
                disabled={timingLocked && Boolean(form.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    sx={autocompleteSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Autocomplete
                options={slideOptions}
                loading={slidesLoading}
                value={selectedSlide}
                onChange={(_, next) =>
                  setForm((f) => ({ ...f, slide_id: next?.value || "" }))
                }
                disabled={timingLocked && Boolean(form.id)}
                getOptionLabel={(o) => o?.label || ""}
                isOptionEqualToValue={(a, b) => a?.value === b?.value}
                filterOptions={(options, state) => {
                  const q = String(state.inputValue || "")
                    .trim()
                    .toLowerCase();
                  if (!q) return options;
                  return options.filter((o) => o.searchText.includes(q));
                }}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    key={option.value}
                    sx={{
                      display: "flex !important",
                      alignItems: "center",
                      gap: 1.5,
                      minHeight: 64,
                      py: 1,
                    }}
                  >
                    <SlideThumb url={option.thumb} size={48} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {option.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.value.slice(0, 8)}…
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Slide"
                    placeholder="Search slides…"
                    sx={autocompleteSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Q index (1–4)"
                value={form.question_index}
                onChange={(e) =>
                  setForm((f) => ({ ...f, question_index: e.target.value }))
                }
                disabled={timingLocked}
                helperText={
                  !form.id && form.slide_id
                    ? `Next: Q${Math.min(
                        EXAM_SIMULATOR_MAX_QUESTIONS_PER_SLIDE,
                        nextQuestionIndexForSlide(items, form.slide_id, category),
                      )} of 4`
                    : undefined
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Sort"
                value={form.sort_order}
                onChange={(e) =>
                  setForm((f) => ({ ...f, sort_order: e.target.value }))
                }
                disabled={timingLocked}
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Question text"
                value={form.question_text}
                onChange={(e) =>
                  setForm((f) => ({ ...f, question_text: e.target.value }))
                }
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Expected answer (for future AI grading)"
                value={form.expected_answer}
                onChange={(e) =>
                  setForm((f) => ({ ...f, expected_answer: e.target.value }))
                }
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Video start (sec)"
                value={form.video_start_sec}
                onChange={(e) =>
                  setForm((f) => ({ ...f, video_start_sec: e.target.value }))
                }
                disabled={timingLocked}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Video end (sec)"
                value={form.video_end_sec}
                onChange={(e) =>
                  setForm((f) => ({ ...f, video_end_sec: e.target.value }))
                }
                disabled={timingLocked}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Wait loop start (sec)"
                value={form.wait_start_sec}
                onChange={(e) =>
                  setForm((f) => ({ ...f, wait_start_sec: e.target.value }))
                }
                disabled={timingLocked}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Wait loop end (sec)"
                value={form.wait_end_sec}
                onChange={(e) =>
                  setForm((f) => ({ ...f, wait_end_sec: e.target.value }))
                }
                disabled={timingLocked}
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Slide video end (sec)"
                helperText="Set on Q4 — cut point before next slide / results"
                value={form.slide_video_end_sec}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    slide_video_end_sec: e.target.value,
                  }))
                }
                disabled={timingLocked}
                sx={inputSx}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={advancedEdit}
                    onChange={(e) => setAdvancedEdit(e.target.checked)}
                  />
                }
                label="Unlock timing, index & marker fields (advanced)"
              />
              <Typography variant="caption" color="text.secondary" display="block">
                Default: edit question text and expected answer only. Q index,
                sort order and video seconds are filled automatically (max 4
                questions per slide). Same templates for Histology & Embryology.
                Q4 always includes the slide pin tool — point at the structure
                the learner must describe.
              </Typography>
            </Grid>

            {showMarkerMap ? (
              <Grid item xs={12}>
                <ExamSimulatorQuestionMarkerMap
                  fileUrl={selectedSlide.fileUrl}
                  fileName={selectedSlide.fileName}
                  fileAlias={selectedSlide.fileAlias}
                  fileType={selectedSlide.fileType}
                  maxZoom={selectedSlide.maxZoom}
                  markerLocation={form.marker_location}
                  disabled={markerMapDisabled}
                  onChange={(marker_location) =>
                    setForm((f) => ({ ...f, marker_location }))
                  }
                />
              </Grid>
            ) : selectedSlide?.fileUrl && !isFourthQuestion ? (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Slide pin placement is available on question 4 (“Can you
                  describe the marked area?”).
                </Typography>
              </Grid>
            ) : null}

            <Grid item xs={12}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={onSave}
                  disabled={saving || !form.slide_id || slideAtMax}
                >
                  {form.id
                    ? "Update question"
                    : `Add question Q${form.question_index || 1}`}
                </MDButton>
                {form.id ? (
                  <MDButton
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setAdvancedEdit(false);
                      setForm({ ...emptyForm(), category });
                    }}
                  >
                    Cancel edit
                  </MDButton>
                ) : null}
                {message ? (
                  <Typography variant="caption" color="text.secondary">
                    {message}
                  </Typography>
                ) : null}
              </Stack>
            </Grid>
          </Grid>
        </MDBox>

        <Stack spacing={2}>
          {grouped.length === 0 ? (
            <Typography color="text.secondary">
              No questions yet for {category}.
            </Typography>
          ) : (
            grouped.map(([slideId, qs]) => {
              const meta = slideMetaById.get(slideId);
              const thumb =
                meta?.thumb ||
                slidePreviewUrl({
                  url: qs[0]?.slide?.file_url,
                  name: qs[0]?.slide?.file_name,
                  alias: qs[0]?.slide?.file_alias,
                  type: qs[0]?.slide?.file_type,
                });
              const title =
                meta?.label ||
                qs[0]?.slide?.title_name ||
                `Slide ${slideId.slice(0, 8)}…`;

              return (
                <MDBox
                  key={slideId}
                  p={2}
                  borderRadius="14px"
                  sx={{
                    bgcolor: theme.palette.background.paper,
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    mb={1.5}
                  >
                    <SlideThumb url={thumb} size={64} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={700} noWrap>
                        {title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {slideId}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`${qs.length} question${
                        qs.length === 1 ? "" : "s"
                      }`}
                    />
                    {qs.length < EXAM_SIMULATOR_MAX_QUESTIONS_PER_SLIDE ? (
                      <MDButton
                        size="small"
                        variant="outlined"
                        color="info"
                        onClick={() => startAddQuestionForSlide(slideId)}
                      >
                        Add Q{qs.length + 1}
                      </MDButton>
                    ) : null}
                  </Stack>
                  {qs
                    .slice()
                    .sort((a, b) => a.question_index - b.question_index)
                    .map((q) => (
                      <MDBox
                        key={q.id}
                        py={1.25}
                        sx={{
                          borderTop: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Stack spacing={0.5} flex={1}>
                            <Typography fontWeight={600}>
                              Q{q.question_index}: {q.question_text}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Ask {q.video_start_sec}s–{q.video_end_sec}s · Wait{" "}
                              {q.wait_start_sec}s–{q.wait_end_sec}s
                              {q.slide_video_end_sec
                                ? ` · Slide end ${q.slide_video_end_sec}s`
                                : ""}
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <MDButton
                              size="small"
                              variant="outlined"
                              color="info"
                              onClick={() => onEdit(q)}
                            >
                              Edit
                            </MDButton>
                            <MDButton
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => onDelete(q)}
                            >
                              Delete
                            </MDButton>
                          </Stack>
                        </Stack>
                      </MDBox>
                    ))}
                </MDBox>
              );
            })
          )}
        </Stack>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
